/* Background video stays inert until visible, and stops work offscreen.
   The image remains visible until a decoded video frame is ready. */
if (!customElements.get('pure-video-player')) {
  customElements.define('pure-video-player', class extends HTMLElement {
    connectedCallback() {
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.playButton = this.querySelector('[data-pure-video-play]');
      this.template = this.querySelector('[data-pure-video-template]');
      this.activateFromButton = () => { this.requested = true; this.activate(); };
      this.handleMotionChange = () => {
        this.requested = false;
        this.classList.toggle('pure-video-feature__embed--still', this.motionQuery.matches);
        if (this.motionQuery.matches) this.classList.remove('pure-video-feature__embed--loaded');
        this.updatePlayback();
      };
      this.onVisibility = () => this.updatePlayback();
      this.playButton?.addEventListener('click', this.activateFromButton);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.classList.toggle('pure-video-feature__embed--still', this.motionQuery.matches);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          this.updatePlayback();
        });
        this.observer.observe(this);
      } else { this.visible = true; this.updatePlayback(); }
    }
    disconnectedCallback() {
      this.playButton?.removeEventListener('click', this.activateFromButton);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.observer?.disconnect();
      this.controller?.destroy();
      this.video?.pause();
      this.video?.remove();
      this.video = null;
      this.requested = false;
    }
    eligible() {
      return this.isConnected && this.visible && !document.hidden && (this.requested || !this.motionQuery.matches);
    }
    updatePlayback() {
      if (this.eligible()) this.activate();
      else this.video?.pause();
    }
    activate() {
      if (!this.eligible()) return;
      if (!this.video && this.template) {
        // Media must belong to the active document before WebKit initializes it.
        const fragment = this.ownerDocument.importNode(this.template.content, true);
        this.video = fragment.querySelector('video');
        if (!this.video) return;
        this.controller = window.PureVideo?.prepare(this.video, { separatePoster: true });
        this.video.addEventListener('playing', () => {
          const reveal = () => {
            if (this.eligible() && this.video && !this.video.paused) this.classList.add('pure-video-feature__embed--loaded', 'pure-video-feature__embed--playing');
          };
          if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
          else reveal();
        });
        this.video.addEventListener('pause', () => this.classList.remove('pure-video-feature__embed--playing'));
        this.video.addEventListener('ended', () => {
          if (this.motionQuery.matches) {
            this.requested = false;
            this.classList.remove('pure-video-feature__embed--loaded');
          } else if (this.eligible()) {
            this.video.currentTime = 0;
            this.video.play().catch(() => {});
          }
        });
        this.template.after(fragment);
      }
      this.video?.play().catch(() => {});
    }
  });
}
